import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { staggerContainer, staggerItem } from '@/hooks/useScrollAnimation';

const logoModules = import.meta.glob('@/assets/partners/*.png', { eager: true, import: 'default' }) as Record<string, string>;

const partnerNames: Record<string, string> = {
  'adabi-riyadh': 'Adabi Riyadh',
  'ajel': 'Ajel',
  'al-ahli-fc': 'Al Ahli FC',
  'arab-union': 'Arab Union',
  'brand-usage-logo': 'Brand Usage',
  'channel-profile1': 'Channel Profile 1',
  'channel-profile2': 'Channel Profile 2',
  'colored-logo': 'Partner',
  'cropped-logo3': 'Partner',
  'fdl': 'FDL',
  'gea': 'GEA',
  'general-directorate-prisons': 'General Directorate of Prisons',
  'image-logo': 'Partner',
  'images-logo': 'Partner',
  'imag-logo': 'Partner',
  'imam-university': 'Imam University',
  'kfshrc': 'KFSHRC',
  'kfupm': 'KFUPM',
  'king-fahd-medical-city': 'King Fahd Medical City',
  'king-fahd-security-college': 'King Fahd Security College',
  'king-saud-university': 'King Saud University',
  'ksau-hs': 'KSAU-HS',
  'logo-039': 'Partner',
  'logo-14': 'Partner',
  'logo-1745': 'Partner',
  'logo-2050195': 'Partner',
  'logo-2245': 'Partner',
  'logo-2': 'Partner',
  'logo2x': 'Partner',
  'logo-382356': 'Partner',
  'logo-4': 'Partner',
  'logo-64119': 'Partner',
  'logo-72494': 'Partner',
  'logo-8934': 'Partner',
  'logo-926': 'Partner',
  'logo-a757': 'Partner',
  'logo-ad70': 'Partner',
  'logo-ar': 'Partner',
  'logo-dark': 'Partner',
  'logo-ec72': 'Partner',
  'logo-eo9y': 'Partner',
  'logo-i16': 'Partner',
  'logo-main': 'Partner',
  'logo-minus1': 'Partner',
  'logo-white': 'Partner',
  'neom': 'NEOM',
  'oip-12': 'Partner',
  'oip-13': 'Partner',
  'oip-14': 'Partner',
  'oip-15': 'Partner',
  'oip-16': 'Partner',
  'oip-17': 'Partner',
  'oip-18': 'Partner',
  'oip-19': 'Partner',
  'oip-20': 'Partner',
  'oip-21': 'Partner',
  'oip-22': 'Partner',
  'oip-23': 'Partner',
  'oip-24': 'Partner',
  'oip-25': 'Partner',
  'oip-26': 'Partner',
  'oip-27': 'Partner',
  'oip-28': 'Partner',
  'oip-29': 'Partner',
  'red-crescent': 'Saudi Red Crescent',
  'reu': 'REU',
  'riyad-bank': 'Riyad Bank',
  'riyadh-chamber': 'Riyadh Chamber',
  'riyadh-municipality': 'Riyadh Municipality',
  'royal-commission': 'Royal Commission',
  'sabic': 'SABIC',
  'sab': 'SAB',
  'sadu': 'SADU',
  'saff': 'SAFF',
  'samf': 'SAMF',
  'sami': 'SAMI',
  'saudi-aramco': 'Saudi Aramco',
  'scfhs': 'SCFHS',
  'screenshot-logo': 'Partner',
  'security-aviation': 'Security Aviation',
  'sela': 'Sela',
  'ssc-sports': 'SSC Sports',
  'stc': 'STC',
  'swa': 'SWA',
  'tahkom': 'Tahkom',
  'tamer': 'Tamer',
  'tns': 'TNS',
  'unkno': 'Partner',
  'unknown': 'Partner',
  'unknow': 'Partner',
  'unnamed-logo': 'Partner',
  'wide-logo': 'Partner',
  'wjek-logo': 'Partner',
};

const partners = Object.entries(logoModules)
  .map(([path, src]) => {
    const filename = path.split('/').pop()?.replace('.png', '') || '';
    return {
      name: partnerNames[filename] || 'Partner',
      logo: src,
      filename,
    };
  })
  .sort((a, b) => a.filename.localeCompare(b.filename));

const half = Math.ceil(partners.length / 2);
const row1 = partners.slice(0, half);
const row2 = partners.slice(half);

export const Partners = () => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const gradientX = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  return (
    <section id="partners" className="py-24 lg:py-32 bg-background relative overflow-hidden">
      <motion.div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" animate={{ opacity: [0.8, 1, 0.8] }} transition={{ duration: 3, repeat: Infinity }} />
      <motion.div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" animate={{ opacity: [0.8, 1, 0.8] }} transition={{ duration: 3, repeat: Infinity, delay: 1.5 }} />

      <div ref={containerRef} className="container mx-auto px-6 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={staggerContainer} className="flex flex-col md:flex-row items-start md:items-center justify-between mb-16">
          <motion.div variants={staggerItem} className="flex items-center gap-4 text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-tight text-foreground">
            <img src="/title-icon.png" alt="" className="w-8 h-8 md:w-10 md:h-10" />
            {t('partners.label')}
          </motion.div>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 1 }} className="relative overflow-hidden group space-y-6">
        <div className="overflow-hidden" style={{ direction: 'ltr' }}>
          <div className="inline-flex animate-marquee group-hover:[animation-play-state:paused]">
            {[...row1, ...row1].map((partner, index) => (
              <motion.div key={index} className="flex-shrink-0 mx-6 px-4 py-4 flex items-center justify-center" whileHover={{ scale: 1.15, y: -5, transition: { duration: 0.3 } }}>
                <motion.img
                  src={partner.logo}
                  alt={partner.name}
                  loading="lazy"
                  data-testid={`img-partner-${index}`}
                  className="h-16 md:h-24 w-auto max-w-[160px] md:max-w-[220px] object-contain filter grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all duration-500"
                  whileHover={{ filter: 'grayscale(0) drop-shadow(0 0 15px hsl(290 46% 49% / 0.5))' }}
                />
              </motion.div>
            ))}
          </div>
        </div>

        <div className="overflow-hidden" style={{ direction: 'ltr' }}>
          <div className="inline-flex animate-marquee-reverse group-hover:[animation-play-state:paused]">
            {[...row2, ...row2].map((partner, index) => (
              <motion.div key={index} className="flex-shrink-0 mx-6 px-4 py-4 flex items-center justify-center" whileHover={{ scale: 1.15, y: -5, transition: { duration: 0.3 } }}>
                <motion.img
                  src={partner.logo}
                  alt={partner.name}
                  loading="lazy"
                  data-testid={`img-partner-row2-${index}`}
                  className="h-16 md:h-24 w-auto max-w-[160px] md:max-w-[220px] object-contain filter grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all duration-500"
                  whileHover={{ filter: 'grayscale(0) drop-shadow(0 0 15px hsl(290 46% 49% / 0.5))' }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      <div className="container mx-auto px-6 lg:px-16">
      </div>
    </section>
  );
};
